import { Fragment } from 'react';
import { cn } from '@ver.id/libs.ui.charlie';
import { useStackWhenTooWide } from '../../hooks/useStackWhenTooWide';

/** A link in the help row: in-card navigation, or an external page in a new tab. */
export type HelpLink =
  { label: string; onClick: () => void } | { label: string; href: string };

/**
 * EmbeddedNeedHelpSection — One or two text links, centred, with a dot
 * between. When they don't fit on one line (a long app name), they stack
 * without the dot, so a link never breaks inside its text.
 */
export default function EmbeddedNeedHelpSection({
  links,
}: {
  links: HelpLink[];
}) {
  const { ref, stacked } = useStackWhenTooWide<HTMLDivElement>(
    links.map((link) => link.label).join('\n'),
  );
  const linkClassName = cn(
    'inline-flex min-h-(--embedded-link-h) items-center px-(--embedded-link-px) text-sm font-medium text-brand-primary underline underline-offset-[3px] hover:text-foreground',
    stacked ? 'text-center' : 'shrink-0 whitespace-nowrap',
  );

  return (
    <div
      ref={ref}
      className={cn(
        'flex w-full items-center justify-center',
        stacked ? 'flex-col' : 'gap-1',
      )}
    >
      {links.map((link, i) => (
        <Fragment key={link.label}>
          {i > 0 && !stacked && (
            <span aria-hidden="true" className="shrink-0 text-muted-foreground">
              ·
            </span>
          )}
          {'href' in link ? (
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClassName}
            >
              {link.label}
            </a>
          ) : (
            <button
              type="button"
              onClick={link.onClick}
              className={linkClassName}
            >
              {link.label}
            </button>
          )}
        </Fragment>
      ))}
    </div>
  );
}
